(() => {
  // ---------------- config & helpers ----------------
  const getMeta = (n) => document.querySelector(`meta[name="${n}"]`)?.getAttribute('content') || null;
  const SF_ENDPOINT = getMeta('storefront-api-endpoint') || '/api/2024-07/graphql.json';
  const SF_TOKEN = getMeta('storefront-api-token');

  // Tweak this to your exact article number format if needed
  const looksLikeSku = (s) => /^[A-Z0-9][A-Z0-9\-._/ ]{1,63}$/i.test((s || '').trim());

  const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  const gidToId = (gid) => (gid || '').split('/').pop();

  async function lookupSku(q) {
    if (!SF_TOKEN) return null;
    const query = `
      query($q: String!) {
        productVariants(first: 5, query: $q) {
          edges {
            node {
              id
              sku
              product {
                handle
                title
                vendor
                featuredImage { url(width: 210), width, height, altText }
              }
            }
          }
        }
      }
    `;
    const clauses = [
      `sku:"${q.replace(/"/g, '\\"')}"`,
      `sku:${q}`
    ];
    for (const clause of clauses) {
      const res = await fetch(SF_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SF_TOKEN
        },
        body: JSON.stringify({ query, variables: { q: clause } }),
        credentials: 'same-origin'
      }).catch(() => null);
      if (!res || !res.ok) continue;
      const json = await res.json().catch(() => null);
      const edges = json?.data?.productVariants?.edges || [];
      if (!edges.length) continue;
      const exact = edges.find(e => (e?.node?.sku || '').trim().toLowerCase() === q.toLowerCase());
      const node = (exact || edges[0])?.node;
      if (node?.product?.handle && node?.id) return node;
    }
    return null;
  }

  function buildDirectMatchLI(variantNode) {
    const vId = gidToId(variantNode.id);
    const href = `/products/${variantNode.product.handle}?variant=${encodeURIComponent(vId)}`;
    const img = variantNode.product.featuredImage;

    const li = document.createElement('li');
    li.className = 'predictive-search__product-item line-item line-item--centered';
    li.id = 'predictive-sku-direct-match';

    li.innerHTML = `
      <a href="${href}" class="line-item__content-wrapper" data-instant>
        ${img ? `
          <span class="line-item__image-wrapper">
            <img class="line-item__image" alt="${(img.altText||'').replace(/"/g,'&quot;')}" width="${img.width||210}" height="${img.height||210}" src="${img.url}">
          </span>` : ``
        }
        <div class="line-item__info">
          <div class="product-item-meta">
            ${variantNode.product.vendor ? `<span class="product-item-meta__vendor heading heading--xxsmall">${variantNode.product.vendor}</span>` : ``}
            <span class="product-item-meta__title text--small">${variantNode.product.title}</span>
            <div class="text--xsmall text--subdued" style="margin-top:2px;">Direct match: <strong>${variantNode.sku}</strong></div>
          </div>
        </div>
        <svg aria-hidden="true" focusable="false" class="icon icon--nav-arrow-right" viewBox="0 0 16 16"><path d="M5 3l5 5-5 5"/></svg>
      </a>
    `;
    return li;
  }

 function ensureProductsList(container) {
  // Prefer the dedicated host we added in the Liquid template
  let ul = container.querySelector('#predictive-sku-host');
  if (ul) return ul;

  // Fallback to theme's products list if present
  ul = container.querySelector('ul.predictive-search__product-list[data-type="products"]');
  if (ul) return ul;

  // Last resort: create a minimal container at the top
  ul = document.createElement('ul');
  ul.className = 'predictive-search__product-list list--unstyled';
  ul.setAttribute('role', 'list');
  ul.setAttribute('data-type', 'products');

  const wrap = document.createElement('div');
  wrap.className = 'predictive-search__results-categories-item';
  wrap.id = 'predictive-sku-injected-products';
  wrap.appendChild(ul);
  container.prepend(wrap);

  return ul;
}

function injectDirectMatch(container, variantNode) {
  const ul = ensureProductsList(container);
  // Remove prior injected row if any
  ul.querySelector('#predictive-sku-direct-match')?.remove();
  if (!variantNode) return;

  const li = buildDirectMatchLI(variantNode);
  ul.prepend(li);

  // Ensure our slot is visible (in case any code toggles categories)
  const slot = container.querySelector('#predictive-sku-slot');
  if (slot) slot.removeAttribute('hidden');
}

  // Observe container changes to re-inject after Focal re-renders results
  function watchContainer(container, getLatestMatch) {
    const mo = new MutationObserver(() => {
      const latest = getLatestMatch();
      if (latest?.product?.handle) injectDirectMatch(container, latest);
      else injectDirectMatch(container, null);
    });
    mo.observe(container, { childList: true, subtree: true });
    return mo;
  }

  function init() {
    const form = document.getElementById('predictive-search-form');
    const input = form?.querySelector('input[name="q"]') || document.querySelector('.predictive-search__input');
    const resultsWrap = document.querySelector('.predictive-search__results');
    if (!input || !resultsWrap) return;

    let lastQuery = '';
    let lastMatch = null;

    const debounced = debounce(async () => {
      const q = input.value.trim();
      lastQuery = q;

      // If query doesn't look like a SKU, clear injected row
      if (!looksLikeSku(q)) {
        injectDirectMatch(resultsWrap, null);
        lastMatch = null;
        return;
      }

      const match = await lookupSku(q);
      // Avoid race: only apply if still current
      if (q !== lastQuery) return;

      lastMatch = match || null;
      if (resultsWrap.hasAttribute('hidden')) {
        // Focal shows results soon; when it does, MutationObserver will re-insert
        // but if results are already visible, inject now:
      }
      injectDirectMatch(resultsWrap, lastMatch);
    }, 180);

    // Input listener
    input.addEventListener('input', debounced);

    // Re-insert whenever Focal swaps predictive results content
    watchContainer(resultsWrap, () => lastMatch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also re-bind on section loads / page transitions used by Focal
  document.addEventListener('shopify:section:load', init, { passive: true });
  document.addEventListener('page:loaded', init, { passive: true });
})();
